<template>
  <Transition name="slide-fade">
    <div v-if="visible" class="overflow-hidden border-b border-gray-100" data-testid="create-calendar-form">
      <div class="p-3 bg-primary/5">
        <h3 class="text-xs font-semibold text-gray-900 mb-2 flex items-center gap-1.5">
          <i class="pi pi-calendar text-primary text-sm"></i> Create {{ pageTitle }}
        </h3>
        <form @submit.prevent="handleCreate" class="space-y-2.5">
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            <div>
              <label class="block text-xs font-medium text-gray-600 mb-0.5">Session</label>
              <Select v-model="form.session_id" :options="sessionOptions" optionLabel="label"
                optionValue="value" placeholder="Select session"
                :class="['w-full', { 'p-invalid': getErrorMessage('session_id') }]" />
              <small v-if="getErrorMessage('session_id')" class="text-red-500 text-xs mt-0.5 block">
                {{ getErrorMessage('session_id') }}
              </small>
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-600 mb-0.5">{{ termLabel }}</label>
              <Select v-model="form.term_structure_id" :options="termOptions" optionLabel="label"
                optionValue="value" :placeholder="`Select ${termLabel.toLowerCase()}`"
                :class="['w-full', { 'p-invalid': getErrorMessage('term_structure_id') }]" />
              <small v-if="getErrorMessage('term_structure_id')" class="text-red-500 text-xs mt-0.5 block">
                {{ getErrorMessage('term_structure_id') }}
              </small>
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-600 mb-0.5">School Days</label>
              <InputNumber v-model="form.total_school_days" placeholder="e.g. 90"
                :class="['w-full', { 'p-invalid': getErrorMessage('total_school_days') }]" />
              <small v-if="getErrorMessage('total_school_days')" class="text-red-500 text-xs mt-0.5 block">
                {{ getErrorMessage('total_school_days') }}
              </small>
            </div>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div>
              <label class="block text-xs font-medium text-gray-600 mb-0.5">Start Date</label>
              <Calendar v-model="form.started_at" dateFormat="yy-mm-dd" placeholder="Start date"
                :class="['w-full', { 'p-invalid': getErrorMessage('started_at') || errors.started_at }]" />
              <small v-if="getErrorMessage('started_at') || errors.started_at" class="text-red-500 text-xs mt-0.5 block">
                {{ getErrorMessage('started_at') || errors.started_at }}
              </small>
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-600 mb-0.5">End Date</label>
              <Calendar v-model="form.ended_at" dateFormat="yy-mm-dd" placeholder="End date"
                :class="['w-full', { 'p-invalid': getErrorMessage('ended_at') || errors.ended_at }]" />
              <small v-if="getErrorMessage('ended_at') || errors.ended_at" class="text-red-500 text-xs mt-0.5 block">
                {{ getErrorMessage('ended_at') || errors.ended_at }}
              </small>
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-600 mb-0.5">Next Calendar Starts</label>
              <Calendar v-model="form.next_calender_starts_at" dateFormat="yy-mm-dd" placeholder="Optional"
                :class="['w-full', { 'p-invalid': getErrorMessage('next_calender_starts_at') }]" />
              <small v-if="getErrorMessage('next_calender_starts_at')" class="text-red-500 text-xs mt-0.5 block">
                {{ getErrorMessage('next_calender_starts_at') }}
              </small>
            </div>
          </div>
          <div class="flex items-center justify-between pt-1">
            <div class="flex items-center gap-1.5">
              <Checkbox v-model="form.is_active" :binary="true" inputId="is_active_cal" />
              <label for="is_active_cal" class="text-xs text-gray-700 font-medium cursor-pointer select-none">
                Set as active calendar
              </label>
            </div>
            <Button type="submit" size="small" :label="`Create ${termLabel === 'Semester' ? 'Semester' : 'Calendar'}`"
              icon="pi pi-check" :loading="isCreating" data-testid="btn-settings-calendar-create" />
          </div>
        </form>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { useQuery } from '@vue/apollo-composable';
import { useToast } from 'primevue/usetoast';
import { useFormErrors } from '@/composables/useFormErrors';
import { useSessions } from '@/composables/queries/useSessions';
import { useSchoolFeatures } from '@/composables/useSchoolFeatures';
import { useSchoolMutations } from '@/composables/mutations/school/useSchoolMutations';
import { GET_TERM_STRUCTURES } from '@/graphql';
import type { Session, TermStructure } from '@/types/composition-functions';

import { extractGraphQLError } from '@/utils/graphqlError';
defineProps<{ visible: boolean }>();
const emit = defineEmits<{ created: [] }>();
const toast = useToast();

const { createAcademicCalendar } = useSchoolMutations();
const { serverErrors, handleServerErrors, clearErrors, getErrorMessage } = useFormErrors();
const { isTertiary } = useSchoolFeatures();

const { schoolSessions } = useSessions({ getSchoolSessions: { autoFetch: true } });
const { sessions } = schoolSessions;

const termLabel = computed(() => isTertiary.value ? 'Semester' : 'Term');
const pageTitle = computed(() => isTertiary.value ? 'Semester Calendar' : 'Academic Calendar');

const sessionStructureId = computed(() => sessions.value?.[0]?.sessionStructure?.id || null);
const { result: term } = useQuery(GET_TERM_STRUCTURES, { session_structure_id: sessionStructureId });

const termOptions = computed<{ label: string; value: string }[]>(
  () => term.value?.termStructuresForStructure?.map((t: TermStructure) => ({ label: t.name, value: t.id })) || [],
);

const sessionOptions = computed<{ label: string; value: string }[]>(
  () => sessions.value?.map((s: Session) => ({ label: s.name || '', value: s.id })) || [],
);

const isCreating = ref(false);
const errors = ref<Record<string, string>>({});

const form = ref({
  session_id: '',
  term_structure_id: '',
  started_at: undefined as Date | undefined,
  ended_at: undefined as Date | undefined,
  is_active: false,
  total_school_days: undefined as number | undefined,
  next_calender_starts_at: undefined as Date | undefined,
});

function formatDateStr(date: Date | string | undefined): string {
  if (!date) return '';
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

async function handleCreate() {
  clearErrors();
  errors.value = {};
  if (!form.value.started_at) errors.value.started_at = 'Start date is required.';
  if (!form.value.ended_at) errors.value.ended_at = 'End date is required.';
  if (form.value.started_at && form.value.ended_at) {
    if (new Date(form.value.started_at).getTime() >= new Date(form.value.ended_at).getTime()) {
      errors.value.ended_at = 'End date must be after start date.';
    }
  }
  if (Object.keys(errors.value).length) return;

  isCreating.value = true;
  try {
    await createAcademicCalendar({
      ...form.value,
      started_at: formatDateStr(form.value.started_at),
      ended_at: formatDateStr(form.value.ended_at),
      next_calender_starts_at: form.value.next_calender_starts_at
        ? formatDateStr(form.value.next_calender_starts_at)
        : undefined,
    });
    form.value = {
      session_id: '',
      term_structure_id: '',
      started_at: undefined,
      ended_at: undefined,
      is_active: false,
      total_school_days: undefined,
      next_calender_starts_at: undefined,
    };
    toast.add({ severity: 'success', summary: 'Success', detail: 'Calendar created successfully', life: 3000 });
    emit('created');
  } catch (err) {
    handleServerErrors(err);
    if (Object.keys(serverErrors.value).length === 0) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(err, 'Failed to create calendar'), life: 3000 });
    }
  } finally {
    isCreating.value = false;
  }
}
</script>
